/**
 * WeakLinksRollupTile — cross-engine "Top weak links" tile for
 * the CLO home page.
 *
 * Sits above the existing CoachingCommitmentsTile and gives the
 * CLO an at-a-glance view of where the highest-leverage fix lives
 * across all 5 engines (Maturity / LEI / PE / ROE / ROI). Each
 * row navigates to the relevant Health page so the CLO can model
 * the fix in a single click.
 *
 * Hidden when the user has no engine data yet (empty rollup) so
 * the home page doesn't churn for fresh tenants.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertTriangle, ArrowRight, Activity, Heart, Users, BookOpen,
  DollarSign, Target, Sparkles, Loader2, Check, Mail, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { useAuth } from '../../context/AuthContext';
import { useMessaging } from '../../hooks/useMessaging';

const ENGINE_META = {
  maturity: { icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  lei: { icon: BookOpen, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  pe: { icon: Users, color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200' },
  roe: { icon: Heart, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  roi: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
};

// ── Engine → Learning Impact Chain label mapping ──────────────
// Per the Missing Middle rebrand, the user-facing label on every
// engine is the *chain stage* it measures — not the engine code
// or "<X> Health" phrasing. Code identifiers (`row.engine`) stay
// intact so all backend wiring is unaffected.
//
//   lei → LEARNING (LEI Health)
//   pe  → CAPABILITY (PE Health)
//   roe → BEHAVIOR (ROE Health — rolls up Confidence/Application/Behavior)
//   roi → FINANCIAL IMPACT (ROI)
//   maturity → MATURITY (sits outside the chain — keep as-is)
const ENGINE_CHAIN_LABEL = {
  lei: 'LEARNING',
  pe: 'CAPABILITY',
  roe: 'BEHAVIOR',
  roi: 'FINANCIAL IMPACT',
  maturity: 'MATURITY',
};
const chainLabelFor = (engine, fallback) =>
  ENGINE_CHAIN_LABEL[engine] || (fallback || '').toUpperCase();

// ── Missing Middle stage filter chips ─────────────────────────
// One chip per chain stage that can actually surface a leak in
// the rollup (maturity sits outside the chain so it has no chip).
// `key` matches the value returned by chainLabelFor() so we can
// filter `rows` with a single equality check. `short` is the
// compact pill label; `key` stays the canonical chain stage.
const STAGE_CHIPS = [
  { key: 'LEARNING', short: 'LEARNING', engine: 'lei' },
  { key: 'CAPABILITY', short: 'CAPABILITY', engine: 'pe' },
  { key: 'BEHAVIOR', short: 'BEHAVIOR', engine: 'roe' },
  { key: 'FINANCIAL IMPACT', short: 'FINANCIAL', engine: 'roi' },
];

const formatScore = (row) => {
  if (typeof row.score !== 'number') return '—';
  if (row.metric_suffix === 'x') return `${row.score}x`;
  if (row.signed) {
    const sign = row.score >= 0 ? '+' : '';
    return `${sign}${row.score.toFixed(1)} ${row.metric_suffix}`;
  }
  return `${Math.round(row.score)}${row.metric_suffix}`;
};

/**
 * 4-week open-rate sparkline — inline SVG (no recharts) so the tile
 * stays dependency-light. Each bar's height encodes open_rate 0..1
 * and colour encodes direction (amber baseline; emerald on the
 * latest bar when trend is "up"; rose when "down"). Hover tooltips
 * via <title> let power users see exact sent/opened counts.
 */
function OpenRateSparkline({ weeks, direction }) {
  if (!weeks || weeks.length < 2) return null;
  const W = 96, H = 22, PAD = 2;
  const barGap = 2;
  const barW = Math.max(4, (W - 2 * PAD - barGap * (weeks.length - 1)) / weeks.length);
  const barColor = (isLast) => {
    if (!isLast) return '#FCD34D'; // amber-300
    if (direction === 'up') return '#10B981'; // emerald-500
    if (direction === 'down') return '#F43F5E'; // rose-500
    return '#FCD34D';
  };
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '▬';
  const arrowColor = direction === 'up'
    ? 'text-emerald-700'
    : direction === 'down' ? 'text-rose-700' : 'text-slate-500';
  return (
    <div
      className="flex items-center gap-2 pt-1.5 mt-1.5 border-t border-white/60"
      data-testid="receipts-trend-sparkline"
    >
      <span className="text-[10px] text-slate-500 uppercase tracking-wider">
        4-wk trend
      </span>
      <svg
        width={W}
        height={H}
        className="inline-block align-middle"
        aria-label="Open-rate 4-week trend"
      >
        {weeks.map((w, i) => {
          const x = PAD + i * (barW + barGap);
          const h = Math.max(1, (w.open_rate || 0) * (H - 2 * PAD));
          const y = H - PAD - h;
          const isLast = i === weeks.length - 1;
          return (
            <rect
              key={w.week_start}
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={barColor(isLast)}
              rx={1}
            >
              <title>
                {`Week of ${w.week_start}: ${w.opened}/${w.sent} opens (${Math.round((w.open_rate || 0) * 100)}%)`}
              </title>
            </rect>
          );
        })}
      </svg>
      <span className={`text-[10px] font-semibold ${arrowColor}`} aria-label={`trend ${direction}`}>
        {arrow} {direction}
      </span>
    </div>
  );
}

// Build the deep-link URL — query params preserve org context;
// scenario_id surfaces a specific scenario in the ROI page.
const buildHref = (row) => {
  const dl = row.deep_link || {};
  const qs = new URLSearchParams();
  if (dl.query) {
    Object.entries(dl.query).forEach(([k, v]) => {
      if (v != null && v !== '') qs.set(k, v);
    });
  }
  if (dl.scenario_id) qs.set('scenario_id', dl.scenario_id);
  const tail = qs.toString();
  return `${dl.path || '/'}${tail ? `?${tail}` : ''}`;
};

export default function WeakLinksRollupTile() {
  const navigate = useNavigate();
  // `viewAsOrg` is populated when the signed-in super-admin has
  // chosen to preview a specific tenant (via the Header's "Switch
  // Org" menu). When set, the receipts endpoints scope to that
  // org — so we surface a small "viewing as Acme" badge on the
  // strip to prevent the "whose receipts am I seeing?" confusion
  // during live CS sessions.
  const { viewAsOrg } = useAuth();
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState(null);
  // Selected Missing Middle stage filter. `null` = ALL (default
  // view shows the global Top 3 across every stage); a stage key
  // (e.g. 'LEARNING') narrows the list to that single stage of
  // the chain so a CLO can investigate one link at a time.
  const [selectedStage, setSelectedStage] = useState(null);
  // Quarterly fix-plan flow state — `'idle' | 'generating' | 'done'`.
  // After success we keep the "View deck" affordance visible so
  // the CLO can jump straight from "I want this" → board view.
  const [planState, setPlanState] = useState('idle');
  const [generatedPinIds, setGeneratedPinIds] = useState([]);
  // Email-the-plan state — shown only after a plan has been
  // generated. The CLO types in 1..10 recipient emails (comma or
  // newline separated), confirms, and the backend ships the
  // styled HTML email + share-link to each one via Resend.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailDone, setEmailDone] = useState(null); // null | {sent, failed, share_url, send_id}
  // Read-receipt state — keyed off send_id from the LAST send.
  // Polled every 30s while emailDone is set, plus on-demand
  // refresh + after a "resend to non-openers" click.
  const [receipts, setReceipts] = useState(null);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [resending, setResending] = useState(false);
  // 4-week open-rate trend — fetched once when the receipts strip
  // becomes visible. Sparkline renders only when there's ≥2 weeks
  // of data so a single send doesn't show a misleading "trend".
  const [trend, setTrend] = useState(null);
  // CSV export state — plain boolean spinner flag; the download
  // is a one-shot blob trigger, no progress stream needed.
  const [exportingCsv, setExportingCsv] = useState(false);
  // Export window (days) — segmented 30/90/365 control beside
  // the export button so the CLO downloads exactly the
  // board-meeting window they need without URL tweaking.
  const [exportDays, setExportDays] = useState(90);

  // Filter-strip helper copy — editable in /admin/messaging so the
  // chain nomenclature stays consistent with the rest of the app.
  const filterLabel = useMessaging(
    'weak_link.filter_label',
    'Filter by chain stage',
  );

  useEffect(() => {
    let alive = true;
    // Fetch a deeper slice (top 8) than we show by default so the
    // stage filter chips have enough leaks to be meaningful. The
    // ALL view still collapses this to the global Top 3.
    api.get('/home/weak-links-rollup?limit=8')
      .then((res) => {
        if (alive) setRows(res?.data?.rows || []);
      })
      .catch((e) => {
        logger.error('weak-links rollup fetch failed:', e);
        if (alive) setError(true);
      });
    return () => { alive = false; };
  }, []);

  const handleGeneratePlan = async () => {
    if (planState === 'generating' || !rows || rows.length === 0) return;
    // The plan pins the global Top 3 regardless of the active
    // stage filter (the endpoint uses limit=3), so message the
    // count that will actually be pinned — not the deeper set we
    // fetched to power the filter chips.
    const planCount = Math.min(3, rows.length);
    if (!window.confirm(
      `Generate a Quarterly Fix Plan from your top ${planCount} weak link${planCount === 1 ? '' : 's'}?\n\nEach will be pinned to your Quarterly Deck with an AI coaching paragraph and a 3-step action plan, ready to share with the Steering Committee.`,
    )) return;
    setPlanState('generating');
    try {
      const res = await api.post('/home/quarterly-fix-plan?limit=3');
      const pinned = res?.data?.pinned ?? 0;
      setGeneratedPinIds(res?.data?.pin_ids || []);
      toast.success(`Pinned ${pinned} weak link${pinned === 1 ? '' : 's'} to your Quarterly Deck`);
      setPlanState('done');
    } catch (e) {
      logger.error('quarterly fix plan failed:', e);
      toast.error("Couldn't generate the plan — try again");
      setPlanState('idle');
    }
  };

  const handleEmailPlan = async () => {
    // Parse comma- and newline-separated emails. Email validation
    // is best-effort here (server validates again); we just dedupe
    // and trim so a sloppy paste doesn't fail.
    const recipients = Array.from(new Set(
      emailRecipients
        .split(/[,\n;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@')),
    ));
    if (recipients.length === 0) {
      toast.error('Add at least one recipient email');
      return;
    }
    if (recipients.length > 10) {
      toast.error('Max 10 recipients per send — split into batches');
      return;
    }
    setEmailSending(true);
    try {
      const res = await api.post('/home/quarterly-fix-plan/email', {
        recipients,
        pin_ids: generatedPinIds.length > 0 ? generatedPinIds : undefined,
      });
      const sent = res?.data?.sent || [];
      const failed = res?.data?.failed || [];
      const sendId = res?.data?.send_id;
      setEmailDone({ sent, failed, share_url: res?.data?.share_url, send_id: sendId });
      if (sent.length > 0 && failed.length === 0) {
        toast.success(`Emailed to ${sent.length} recipient${sent.length === 1 ? '' : 's'}`);
      } else if (sent.length > 0) {
        toast.warning(`Sent ${sent.length}, failed ${failed.length}`);
      } else {
        toast.error('All sends failed — check email config (RESEND_API_KEY)');
      }
    } catch (e) {
      logger.error('email fix plan failed:', e);
      toast.error("Couldn't send — try again");
    } finally {
      setEmailSending(false);
    }
  };

  const refreshReceipts = useCallback(async (sendId) => {
    if (!sendId) return;
    setReceiptsLoading(true);
    try {
      const res = await api.get(
        `/home/quarterly-fix-plan/email/receipts?send_id=${encodeURIComponent(sendId)}`,
      );
      setReceipts(res?.data || null);
    } catch (e) {
      logger.error('receipts fetch failed:', e);
    } finally {
      setReceiptsLoading(false);
    }
  }, []);

  // Poll receipts every 30s while a recent send is on the screen.
  // Stops as soon as emailDone is cleared (modal closed) so we
  // don't burn requests forever in the background.
  useEffect(() => {
    if (!emailDone?.send_id) return;
    refreshReceipts(emailDone.send_id);
    const id = setInterval(() => refreshReceipts(emailDone.send_id), 30000);
    return () => clearInterval(id);
  }, [emailDone?.send_id, refreshReceipts]);

  // Fetch the 4-week open-rate trend once the receipts strip
  // becomes visible. Cheap query (≤12 tokens per week) so we
  // refetch alongside every receipts refresh to keep the
  // sparkline warm as opens trickle in.
  useEffect(() => {
    if (!emailDone?.send_id) { setTrend(null); return; }
    let alive = true;
    api.get('/home/quarterly-fix-plan/email/receipts-trend?weeks=4')
      .then((res) => { if (alive) setTrend(res?.data || null); })
      .catch((e) => { logger.error('receipts trend fetch failed:', e); });
    return () => { alive = false; };
  }, [emailDone?.send_id, receipts?.opened]);

  // One-shot CSV download of the last 90 days of receipts. We use
  // fetch + blob so we can set a nice filename and trigger a true
  // download (no navigation). Runs through the same auth as the
  // rest of the tile — api.defaults.headers carries the bearer.
  const handleExportCsv = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const res = await api.get(
        `/home/quarterly-fix-plan/email/receipts-csv?days=${exportDays}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `quarterly-fix-plan-receipts-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const rowCount = res?.headers?.['x-row-count'] ?? '?';
      toast.success(`Exported ${rowCount} receipt row${rowCount === '1' ? '' : 's'}`);
    } catch (e) {
      logger.error('receipts csv export failed:', e);
      toast.error("Couldn't export CSV — try again");
    } finally {
      setExportingCsv(false);
    }
  };

  // Copy non-opener emails to the clipboard — one-shot helper so
  // CLOs can fire a quick 1:1 ping from their own Outlook/Gmail
  // instead of going back through the Resend flow. Silent no-op
  // when everyone already opened.
  const handleCopyNonOpeners = async () => {
    const list = (receipts?.recipients || [])
      .filter((r) => !r.opened && r.email)
      .map((r) => r.email);
    if (list.length === 0) {
      toast.info('Nobody left to chase — every recipient has opened.');
      return;
    }
    try {
      await navigator.clipboard.writeText(list.join(', '));
      toast.success(`Copied ${list.length} non-opener email${list.length === 1 ? '' : 's'}`);
    } catch (e) {
      logger.error('clipboard copy failed:', e);
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };

  const handleResendNonOpeners = async () => {    if (!emailDone?.send_id || resending) return;
    if (!window.confirm(
      `Resend the plan to non-openers from this batch?\n\nNew per-recipient tracking links will be minted; the original links stay live so a delayed open still counts on the first send.`,
    )) return;
    setResending(true);
    try {
      const res = await api.post(
        `/home/quarterly-fix-plan/email/resend-non-openers?send_id=${encodeURIComponent(emailDone.send_id)}`,
      );
      const nonOpeners = res?.data?.non_opener_count ?? 0;
      const sentCount = (res?.data?.sent || []).length;
      if (nonOpeners === 0) {
        toast.info('Everyone already opened the original send — nothing to resend.');
      } else if (sentCount > 0) {
        toast.success(`Resent to ${sentCount} non-opener${sentCount === 1 ? '' : 's'}`);
      } else {
        toast.error('Resend failed — check email config');
      }
      // Switch over to the new send_id so the receipt strip
      // reflects the latest batch's progress.
      if (res?.data?.send_id && nonOpeners > 0) {
        setEmailDone({
          sent: res?.data?.sent || [],
          failed: res?.data?.failed || [],
          share_url: res?.data?.share_url || '',
          send_id: res?.data?.send_id,
        });
        setReceipts(null);
      }
    } catch (e) {
      logger.error('resend non-openers failed:', e);
      toast.error("Couldn't resend — try again");
    } finally {
      setResending(false);
    }
  };

  // Format relative time for "last view 12 min ago" copy. We
  // intentionally cap at "yesterday" — anything older than 24h
  // means the read-receipt strip has stopped being useful as a
  // pre-board-meeting signal anyway.
  const relativeTime = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return 'yesterday or earlier';
  };

  // Loading state suppressed — tile slot collapses to nothing
  // until data arrives so the home page doesn't reflow.
  if (rows === null || error || rows.length === 0) return null;

  // Per-stage leak counts (computed over the full fetched set) so
  // each chip can show a count badge and empty stages can dim out.
  const stageCounts = STAGE_CHIPS.reduce((acc, chip) => {
    acc[chip.key] = rows.filter((r) => chainLabelFor(r.engine, r.engine_label) === chip.key).length;
    return acc;
  }, {});

  // Rows actually rendered: ALL → global Top 3; a stage → every
  // leak in that stage. We attach the row's *global* index so the
  // "rank #N" badge always reflects chain-wide severity, even when
  // filtered to a single stage.
  const indexedRows = rows.map((row, idx) => ({ row, rank: idx + 1 }));
  const displayedRows = selectedStage
    ? indexedRows.filter(({ row }) => chainLabelFor(row.engine, row.engine_label) === selectedStage)
    : indexedRows.slice(0, 3);

  return (
    <Card
      className="border-rose-200 bg-gradient-to-br from-white to-rose-50/40"
      data-testid="weak-links-rollup-tile"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
            {selectedStage
              ? `${stageCounts[selectedStage]} ${selectedStage} value leak${stageCounts[selectedStage] === 1 ? '' : 's'}`
              : `Top ${displayedRows.length} value leak${displayedRows.length === 1 ? '' : 's'} across the Learning Impact Chain`}
            <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-rose-600 border-rose-300 ml-1">
              {selectedStage ? 'stage view' : 'chain-wide'}
            </Badge>
          </CardTitle>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Where to spend your next quarter for the biggest lift. Each row maps to a stage of the chain. Click any row to model the fix.
        </p>

        {/* Missing Middle stage filter — one chip per chain stage.
            ALL restores the global Top 3; a stage chip narrows the
            list to that single link so the CLO can investigate one
            stage of the chain at a time. Empty stages dim out. */}
        <div
          className="flex items-center gap-1.5 flex-wrap pt-2.5"
          data-testid="weak-links-stage-filter"
          role="group"
          aria-label={filterLabel}
        >
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-0.5">
            {filterLabel}
          </span>
          <button
            type="button"
            onClick={() => setSelectedStage(null)}
            aria-pressed={selectedStage === null}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors ${
              selectedStage === null
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
            }`}
            data-testid="weak-links-stage-chip-all"
          >
            All
          </button>
          {STAGE_CHIPS.map((chip) => {
            const count = stageCounts[chip.key] || 0;
            const empty = count === 0;
            const active = selectedStage === chip.key;
            const meta = ENGINE_META[chip.engine];
            return (
              <button
                key={chip.key}
                type="button"
                disabled={empty}
                onClick={() => setSelectedStage(active ? null : chip.key)}
                aria-pressed={active}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : empty
                      ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                      : `bg-white ${meta.color} ${meta.border} hover:shadow-sm`
                }`}
                data-testid={`weak-links-stage-chip-${chip.engine}`}
                title={empty ? `No ${chip.key} leaks in the top 8` : `Show ${chip.key} leaks`}
              >
                {chip.short}
                <span className={`tabular-nums ${active ? 'text-white/80' : empty ? 'text-slate-300' : 'text-slate-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayedRows.map(({ row, rank }, i) => {
          const meta = ENGINE_META[row.engine] || ENGINE_META.maturity;
          const Icon = meta.icon;
          const href = buildHref(row);
          return (
            <Link
              key={`${row.engine}-${row.entity_id}-${rank}`}
              to={href}
              onClick={(e) => {
                // Use programmatic nav so we can fire telemetry
                // before navigating (best-effort — never blocks).
                e.preventDefault();
                api.post('/analytics/weak-link/snooze', {
                  source: 'home_rollup_click',
                  pillar_id: `${row.engine}:${row.entity_id}`,
                  days: 0,
                }).catch(() => {});
                navigate(href);
              }}
              className={`group flex items-center gap-3 p-3 rounded-md border bg-white ${meta.border} hover:shadow-sm transition-all`}
              data-testid={`weak-links-rollup-row-${i}`}
            >
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${meta.bg}`}>
                <Icon className={`h-4 w-4 ${meta.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${meta.color}`}>
                    {chainLabelFor(row.engine, row.engine_label)}
                  </span>
                  <span className="text-[10px] text-slate-400">·</span>
                  <span className="text-[10px] text-slate-500 tabular-nums">
                    rank #{rank}
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {row.entity_label}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-bold text-rose-700 tabular-nums leading-tight">
                  {formatScore(row)}
                </p>
                <p className="text-[10px] text-slate-500 leading-tight">
                  current
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-rose-600 group-hover:translate-x-0.5 transition-all shrink-0" />
            </Link>
          );
        })}
        {/* Quarterly Fix Plan — one-click pin-all. Sits below the
            row list so the CLO has scanned the priorities before
            they commit to broadcasting them to the deck. */}
        <p className="text-[11px] text-slate-500 italic flex items-center gap-1.5 pt-1 leading-snug">
          <Activity className="h-3 w-3" />
          Sorted by severity. Lower-scoring entities surface first.
        </p>

        {/* Read-receipt strip — shown after a send, always visible
            on the rollup tile (not just inside the modal) so the
            CLO sees inbox progress at a glance whenever they
            return to the home page. Auto-polls every 30s. */}
        {receipts && receipts.total > 0 && (
          <div
            className={`mt-2 rounded-md border p-2.5 ${
              receipts.opened === receipts.total
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
            }`}
            data-testid="quarterly-fix-plan-receipts-strip"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Mail className={`h-3.5 w-3.5 ${
                  receipts.opened === receipts.total ? 'text-emerald-700' : 'text-amber-700'
                }`} />
                <span className="text-[11px] font-semibold text-slate-800 tabular-nums">
                  <span data-testid="receipts-opened-count">{receipts.opened}</span>
                  {' '}of {receipts.total} board members opened
                </span>
                {viewAsOrg?.name && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider"
                    data-testid="receipts-viewing-as-badge"
                    title={`Super-admin preview: showing ${viewAsOrg.name}'s board-email engagement, scoped to that tenant only.`}
                  >
                    viewing as {viewAsOrg.name}
                  </span>
                )}
                {receipts.last_view_at && (
                  <span className="text-[10px] text-slate-500">
                    · last view {relativeTime(receipts.last_view_at)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => refreshReceipts(emailDone?.send_id)}
                  disabled={receiptsLoading}
                  className="text-[10px] text-slate-500 hover:text-slate-800 disabled:opacity-50"
                  data-testid="receipts-refresh-btn"
                >
                  refresh
                </button>
                <span className="text-[10px] text-slate-300">·</span>
                <div
                  role="group"
                  aria-label="CSV export window"
                  className="inline-flex items-center rounded border border-slate-300 bg-white/70 overflow-hidden"
                  data-testid="receipts-export-days-control"
                >
                  {[30, 90, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExportDays(d)}
                      aria-pressed={exportDays === d}
                      className={`text-[10px] px-1.5 py-0.5 transition-colors ${
                        exportDays === d
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                      data-testid={`receipts-export-days-${d}`}
                    >
                      {d === 365 ? '1y' : `${d}d`}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={exportingCsv}
                  className="text-[10px] text-slate-500 hover:text-slate-800 disabled:opacity-50"
                  data-testid="receipts-export-csv-btn"
                  title={`Download the last ${exportDays} days of receipts as CSV`}
                >
                  {exportingCsv ? 'exporting…' : 'export CSV'}
                </button>
                {receipts.opened < receipts.total && (
                  <>
                    <span className="text-[10px] text-slate-300">·</span>
                    <button
                      type="button"
                      onClick={handleCopyNonOpeners}
                      className="text-[10px] text-slate-500 hover:text-slate-800"
                      data-testid="receipts-copy-non-openers-btn"
                      title="Copy non-opener emails to the clipboard for a 1:1 ping"
                    >
                      copy {receipts.total - receipts.opened} email{receipts.total - receipts.opened === 1 ? '' : 's'}
                    </button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleResendNonOpeners}
                      disabled={resending}
                      data-testid="receipts-resend-btn"
                      className="ml-1 text-[10px] h-6 px-2 bg-slate-900 hover:bg-slate-800 text-white"
                    >
                      {resending ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : null}
                      Resend to non-openers ({receipts.total - receipts.opened})
                    </Button>
                  </>
                )}
              </div>
            </div>
            {/* 4-week open-rate sparkline — a tiny horizontal bar
                strip under the "N of M opened" line so the CLO
                can spot at a glance whether board attention is
                climbing or drifting over time. Rendered only when
                there are at least 2 weeks of signal. */}
            {trend && (trend.weeks || []).some((w) => w.sent > 0) && trend.weeks.length >= 2 && (
              <OpenRateSparkline
                weeks={trend.weeks}
                direction={trend.trend_direction}
              />
            )}
          </div>
        )}

        <div className="pt-2 border-t border-rose-100 flex items-center justify-between gap-2 flex-wrap">
          {planState === 'done' ? (
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  navigate('/');
                  // React Router doesn't auto-scroll to hash anchors,
                  // so we wait one tick for the route to mount then
                  // scroll the commitments card into view.
                  setTimeout(() => {
                    document
                      .getElementById('coaching-commitments')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 250);
                }}
                data-testid="quarterly-fix-plan-view-btn"
                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Pinned · view deck
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => { setEmailOpen(true); setEmailDone(null); }}
                data-testid="quarterly-fix-plan-email-btn"
                className="text-xs bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Email Steering Committee
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={handleGeneratePlan}
              disabled={planState === 'generating'}
              data-testid="quarterly-fix-plan-generate-btn"
              className="text-xs bg-rose-600 hover:bg-rose-700 text-white"
            >
              {planState === 'generating' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              )}
              {planState === 'generating' ? 'Generating…' : 'Generate Quarterly Fix Plan'}
            </Button>
          )}
          <p className="text-[10px] text-slate-500 italic flex items-center gap-1.5 leading-snug ml-auto">
            <Activity className="h-3 w-3" />
            Sorted by severity. Lowest scores surface first.
          </p>
        </div>

        {/* Email-the-plan modal — opens after the user clicks
            "Email Steering Committee". Plain JSX overlay (no Radix
            Dialog needed for this one) so the rollup tile stays a
            simple, dependency-light home component. */}
        {emailOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
            data-testid="quarterly-fix-plan-email-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setEmailOpen(false); }}
          >
            <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-md w-full p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold flex items-center gap-2 text-slate-900">
                  <Mail className="h-4 w-4 text-slate-700" />
                  Email Quarterly Fix Plan
                </h3>
                <button
                  type="button"
                  onClick={() => setEmailOpen(false)}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="quarterly-fix-plan-email-close"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {emailDone ? (
                <div className="space-y-3" data-testid="quarterly-fix-plan-email-result">
                  {emailDone.sent.length > 0 && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                      <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
                        Sent · {emailDone.sent.length}
                      </p>
                      <p className="text-[12px] text-slate-700 mt-1 break-all">
                        {emailDone.sent.join(', ')}
                      </p>
                    </div>
                  )}
                  {emailDone.failed.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                      <p className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold">
                        Failed · {emailDone.failed.length}
                      </p>
                      <p className="text-[12px] text-slate-700 mt-1 break-all">
                        {emailDone.failed.join(', ')}
                      </p>
                    </div>
                  )}
                  {emailDone.share_url && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                        Share link (copy if needed)
                      </p>
                      <p className="text-[11px] text-slate-700 mt-1 break-all font-mono">
                        {emailDone.share_url}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setEmailOpen(false)}
                      className="bg-slate-900 hover:bg-slate-800 text-white text-xs"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[12px] text-slate-600 mb-2">
                    Recipients · 1–10 emails (comma or newline separated)
                  </p>
                  <textarea
                    value={emailRecipients}
                    onChange={(e) => setEmailRecipients(e.target.value)}
                    placeholder={'cfo@yourorg.com\\nclo@yourorg.com\\nchairman@board.org'}
                    rows={4}
                    className="w-full text-[13px] font-mono p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-500"
                    data-testid="quarterly-fix-plan-email-recipients"
                  />
                  <p className="text-[11px] text-slate-500 mt-2">
                    Each recipient gets an HTML email with the AI coaching paragraphs, 3-step plans, and a 60-day shareable read-only link to the full deck.
                  </p>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEmailOpen(false)}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleEmailPlan}
                      disabled={emailSending}
                      data-testid="quarterly-fix-plan-email-send"
                      className="text-xs bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      {emailSending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Mail className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {emailSending ? 'Sending…' : 'Send'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
